import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AgentAttachmentUploadResponse } from '../../models/agent-action.model';

/**
 * Formatos aceptados por el botón del clip.
 *
 * Espeja la lista blanca del backend para la categoría CHATFLOW_ATTACHMENT: si el navegador
 * ya filtra por extensión, el agente no elige un archivo que el servidor va a rechazar.
 */
const FORMATOS_ACEPTADOS = [
  '.jpg', '.jpeg', '.png', '.webp', '.gif',
  '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.txt', '.csv', '.zip'
].join(',');

/** Tope del almacenamiento del CRM. Se comprueba aquí para no subir 30 MB y fallar al final. */
const MAX_BYTES = 10 * 1024 * 1024;

@Component({
  selector: 'app-agent-conversation-input',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="p-3 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">

      <div *ngIf="isDisabled" class="p-3 bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-center text-gray-500 text-sm">
        <span>{{ disabledMessage }}</span>
        <button *ngIf="showTakeAction" type="button" (click)="takeRequested.emit()" [disabled]="taking"
                class="ml-3 px-3 py-1.5 text-xs font-medium bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300 rounded-lg border border-blue-200 dark:border-blue-800 transition disabled:opacity-50">
          {{ taking ? 'Tomando…' : 'Tomar conversación' }}
        </button>
      </div>

      <ng-container *ngIf="!isDisabled">

        <!-- Error de validación local del archivo -->
        <div *ngIf="fileError" class="mb-2 rounded-lg bg-error-50 dark:bg-error-500/10 border border-error-200 dark:border-error-500/30 px-3 py-2 text-xs text-error-600 dark:text-error-400 flex justify-between items-center">
          <span>{{ fileError }}</span>
          <button (click)="fileError = ''" class="ml-3 font-bold leading-none">&times;</button>
        </div>

        <!-- Subiendo -->
        <div *ngIf="uploading" class="mb-2 flex items-center gap-2 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 px-3 py-2 text-xs text-gray-500">
          <svg class="animate-spin h-4 w-4 text-brand-500" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
          </svg>
          Subiendo archivo…
        </div>

        <!-- Vista previa del adjunto listo para enviar -->
        <div *ngIf="attachment && !uploading"
             class="mb-2 flex items-center gap-3 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 p-2">
          <img *ngIf="esImagen(attachment)" [src]="attachment.url" alt="Vista previa"
               class="h-12 w-12 rounded object-cover shrink-0" />
          <div *ngIf="!esImagen(attachment)"
               class="h-12 w-12 rounded bg-gray-200 dark:bg-gray-700 flex items-center justify-center shrink-0 text-gray-500">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
            </svg>
          </div>
          <div class="min-w-0 flex-1">
            <p class="truncate text-xs font-medium text-gray-800 dark:text-gray-100">{{ attachment.fileName }}</p>
            <p class="text-[10px] text-gray-400">{{ tamanoLegible(attachment.fileSize) }}</p>
          </div>
          <button (click)="attachmentRemoved.emit()" title="Quitar archivo"
                  class="shrink-0 rounded p-1 text-gray-400 hover:text-error-600 transition">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
          </button>
        </div>

        <div class="flex items-end gap-2">
          <!-- inc71: el clip abre el explorador de archivos. Antes era un <button> sin
               ningún manejador: pulsarlo no hacía absolutamente nada. -->
          <input type="file" #fileInput class="hidden" [accept]="formatosAceptados" (change)="onFileChosen($event)" />
          <button type="button" (click)="fileInput.click()"
                  [disabled]="loading || uploading || !!attachment"
                  [title]="attachment ? 'Ya hay un archivo adjunto' : 'Adjuntar archivo (máx. 10 MB)'"
                  class="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition disabled:opacity-40 disabled:cursor-not-allowed">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path></svg>
          </button>

          <div class="flex-1 relative">
            <textarea
              [(ngModel)]="messageText"
              (keydown)="onKeyDown($event)"
              [disabled]="loading"
              class="w-full bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500 resize-none placeholder-gray-400 text-sm"
              rows="1"
              placeholder="Escribe un mensaje de respuesta... (Enter para enviar)"
              style="min-height: 44px; max-height: 120px;"
            ></textarea>
          </div>

          <button
            (click)="send()"
            [disabled]="loading || uploading || (!messageText.trim() && !attachment)"
            class="p-2.5 bg-brand-500 text-white rounded-xl hover:bg-brand-600 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center">
            <svg *ngIf="!loading" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>
            <svg *ngIf="loading" class="animate-spin h-[18px] w-[18px] text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </button>
        </div>
      </ng-container>

    </div>
  `
})
export class AgentConversationInputComponent {
  @Input() isDisabled = false;
  /**
   * Por qué no se puede escribir. Cerrada ya no es el único motivo: desde el filtro «Todos» se
   * abren conversaciones de otros agentes, y ahí el redactor se bloquea hasta tomarlas.
   */
  @Input() disabledMessage = 'Esta conversación está cerrada. No puedes enviar más mensajes.';
  /** Ofrece "Tomar conversación" en el propio bloqueo, que es donde el agente descubre que no puede escribir. */
  @Input() showTakeAction = false;
  @Input() taking = false;
  @Output() takeRequested = new EventEmitter<void>();
  @Input() loading = false;
  /** true mientras el archivo elegido viaja al servidor. */
  @Input() uploading = false;
  /** Archivo ya subido y pendiente de enviar, o null. */
  @Input() attachment: AgentAttachmentUploadResponse | null = null;

  @Output() onSend = new EventEmitter<string>();
  @Output() fileSelected = new EventEmitter<File>();
  @Output() attachmentRemoved = new EventEmitter<void>();

  messageText = '';
  fileError = '';
  readonly formatosAceptados = FORMATOS_ACEPTADOS;

  send() {
    const texto = this.messageText.trim();
    if (this.loading || this.uploading || this.isDisabled) return;
    if (!texto && !this.attachment) return;
    this.onSend.emit(texto);
    this.messageText = ''; // Limpia tras enviar, el padre decide si hubo error en deshacer
  }

  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.send();
    }
  }

  onFileChosen(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    // El input se limpia SIEMPRE: si no, elegir dos veces el mismo archivo no dispara 'change'.
    input.value = '';
    if (!file) return;
    if (file.size > MAX_BYTES) {
      this.fileError = `"${file.name}" pesa ${this.tamanoLegible(file.size)}. El máximo permitido es 10 MB.`;
      return;
    }
    this.fileError = '';
    this.fileSelected.emit(file);
  }

  esImagen(attachment: AgentAttachmentUploadResponse): boolean {
    return (attachment.contentType || '').startsWith('image/');
  }

  tamanoLegible(bytes: number | undefined): string {
    if (!bytes || bytes <= 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
}
